import { IResponseActivity, IResponseAllActivity } from '@/core/domain/entities/activities';
import { IResponseBalance } from '@/core/domain/entities/user';
import * as ViewModel from '@/core/presenter/viewModel';
import { getStores } from '@/stores';
import { truncateString } from '@/utils';
import dayjs from 'dayjs';

/**
 * 원화 금액 계산 및 포맷팅 함수
 * @param amount 거래 금액
 * @param price 가격
 * @returns 포맷팅된 원화 금액 문자열
 */
function formatKrwAmount(amount: number | string, price: number | undefined): string {
  // amount를 숫자로 변환 (문자열인 경우 부호, 공백, 통화 기호 제거)
  let amountValue: number;
  if (typeof amount === 'string') {
    // 부호, 공백, 통화 기호 제거 후 숫자로 변환
    const cleaned = amount.replace(/[+\-\sSOLGDF]/g, '');
    amountValue = Number(cleaned) || 0;
  } else {
    amountValue = amount || 0;
  }

  // 절댓값으로 계산 (부호는 amount 문자열에서 처리)
  const absAmount = Math.abs(amountValue);
  const calculated = absAmount * (price ?? 0);

  // 0보다 작거나 같은 값은 0으로 처리
  if (calculated <= 0) return '0';

  // 소수점 둘째자리까지 버림
  const floored = Math.floor(calculated * 100) / 100;
  const fixed = floored.toString();

  // 정수 부분과 소수 부분 분리
  const [integerPart, decimalPart = ''] = fixed.split('.');

  // 정수 부분에 천 단위 구분자 추가
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // 소수 부분에서 불필요한 0 제거
  const trimmedDecimal = decimalPart.replace(/0+$/, '');

  // 소수 부분이 있으면 합치고, 없으면 정수 부분만 반환
  return trimmedDecimal ? `${formattedInteger}.${trimmedDecimal}` : formattedInteger;
}

/**
 * 트랜잭션 Transfer 함수
 * @param entities
 * @returns
 */
export function toTransactions(entities: IResponseActivity[]): ViewModel.IResponseActivity[] {
  return entities.map((entity) => toTransaction(entity));
}

/**
 * 트랜잭션 Transfer 함수
 * @param entities
 * @returns
 */
export function toTransaction(entities: IResponseActivity): ViewModel.IResponseActivity {
  const { presentationStore } = getStores();
  const timeStamp = entities.timeStamp;
  const ret: ViewModel.IResponseActivity = {
    txID: entities.txId,
    date: dayjs(timeStamp).format('YY.MM.DD HH:mm:ss'),
    from: entities.from,
    to: entities.to,
    from_short: truncateString(entities.from),
    to_short: truncateString(entities.to),
    amount: `${entities.type === '출금' ? '-' : '+'} ${entities.amount} ${entities.coinType}`,
    krwAmount: `${
      entities.coinType === 'coin' || entities.coinType === ViewModel.AssetType.COIN
        ? (entities.amount, presentationStore.balance?.coinPrice)
        : (entities.amount, presentationStore.balance?.tokenPrice)
    } 원`,
    coinType: entities.coinType,
    type: toTransactionTypeText(entities.type),
    imgType: toTransactionImgFileText(entities.type),
    status: toTransactionStatus(entities.status),
    fee: entities.fee.toString(),
    from_memo: entities.from_memo,
    to_memo: entities.to_memo,
  };

  return ret;
}

/**
 * 트랜잭션 Transfer 함수
 * @param entities
 * @returns
 */
export function toAllTransactions(entities: IResponseAllActivity[]): ViewModel.IResponseActivity[] {
  return entities.map((entity) => toAllTransaction(entity));
}

/**
 * 트랜잭션 Transfer 함수
 * @param entities
 * @returns
 */
export function toAllTransaction(entities: IResponseAllActivity): ViewModel.IResponseActivity {
  const { presentationStore } = getStores();
  const timeStamp = entities.timeStamp;
  const ret: ViewModel.IResponseActivity = {
    txID: entities.txId,
    date: dayjs.unix(timeStamp).format('YY.MM.DD HH:mm:ss'),
    from: entities.from,
    to: entities.to,
    from_short: truncateString(entities.from || ''),
    to_short: truncateString(entities.to || ''),
    amount: `${entities.type === '출금' ? '-' : '+'} ${entities.amount} ${toCoinTypeText(entities.coinType)}`,
    krwAmount: `${
      entities.coinType === 'coin' || entities.coinType === ViewModel.AssetType.COIN
        ? formatKrwAmount(entities.amount, presentationStore.balance?.coinPrice)
        : formatKrwAmount(entities.amount, presentationStore.balance?.tokenPrice)
    } 원`,
    coinType: toCoinTypeText(entities.coinType),
    type: toTransactionTypeText(entities.type),
    imgType: toTransactionImgFileText(entities.type),
    status: toTransactionStatus(entities.status),
    fee: entities.fee.toString(),
    from_memo: entities.from_memo,
    to_memo: entities.to_memo,
  };

  return ret;
}

/**
 * 잔액 Transfer 함수
 * @param entities
 * @returns
 */
export function toBalance(entities: IResponseBalance): ViewModel.IResponseBalance {
  // KRW 값들을 소수점 둘째자리까지 버림 처리
  const formatKRW = (value: number): number => {
    return Math.floor(value * 100) / 100;
  };

  return {
    address: entities.address,
    coinBalance: entities.coinBalance,
    coinKRW: formatKRW(entities.coinKRW),
    tokenBalance: entities.tokenBalance,
    tokenKRW: formatKRW(entities.tokenKRW),
    coinPrice: entities.coinPrice,
    tokenPrice: entities.tokenPrice,
    tokenBuyCount: entities.tokenBuyCount,
    tokenBuyCountKRW: formatKRW(entities.tokenBuyCountKRW),
  };
}

/**
 * 트랜잭션 상태 맵핑
 * @param status
 * @returns
 */
function toTransactionStatus(status: string): ViewModel.TransactionStatus | undefined {
  if (status === 'success') return ViewModel.TransactionStatus.SUCCESS;
  if (status === 'error') return ViewModel.TransactionStatus.FAIL;

  return undefined;
}

/**
 * 트랜잭션 이미지 텍스트 맵핑
 * @param type
 * @returns
 */
function toTransactionImgFileText(type: string): string {
  if (type === '출금') return 'send';
  if (type === '입금') return 'rcv';
  return type;
}

/**
 * 트랜잭션 유형 텍스트 맵핑
 * @param type
 * @returns
 */
function toTransactionTypeText(type: string): string {
  if (type === '출금') return '보내기';
  if (type === '입금') return '받기';
  return type;
}

/**
 * 유저 상태 텍스트 변환 함수
 *
 * @export
 * @param {number} status
 * @return {*}  {ViewModel.UserStatus}
 */
export function toUserStatusText(status: number): ViewModel.UserStatus {
  if (status === 0) return ViewModel.UserStatus.DISABLED;
  if (status === 1) return ViewModel.UserStatus.ACTIVE;
  if (status === 2) return ViewModel.UserStatus.PAYMENT;
  if (status === 8) return ViewModel.UserStatus.ADMIN;
  if (status === 9) return ViewModel.UserStatus.WITHDRAWN;

  return ViewModel.UserStatus.DISABLED;
}

/**
 * 코인 타입 텍스트 맵핑
 *
 * @export
 * @param {string} type
 * @return {*}  {string}
 */
export function toCoinTypeText(type: string): string {
  if (type === 'coin') return 'SOL';
  if (type === 'token') return 'GDF';
  return '';
}
